/* ==========================================================================
   sd-search.js - <sd-predictive-search>
   --------------------------------------------------------------------------
   Header search. Native Web Component, no dependencies.

   IT WRAPS A REAL FORM. The element contains an ordinary GET form pointing at
   the search page with a real input named "q". With scripting off, or if this
   file fails to load, typing and pressing Enter runs a normal Shopify search.
   Nothing here is load-bearing.

   RESULTS ARE GROUPED BY BUILD STAGE and every hit carries a brand label,
   because in this catalog "pedals" returns products from five brands at three
   price tiers and an ungrouped list of ten titles is unreadable.

   The stage is derived from the tags the endpoint returns, in the SAME ORDER as
   snippets/sd-build-stage.liquid. If you change one, change the other. The
   order matters: cat-bundle is tested first so a bundle never lands in a
   sequential stage, and foundation before controls so a rig that also carries a
   control tag still reads as the thing you build on.

   INTERNAL VENDOR NAMES NEVER RENDER. The endpoint returns product.vendor, and
   on this store an internal name has leaked into that field on 26 products. This
   filters through an ALLOWLIST of public brand names, not a blocklist: a
   blocklist would have to be printed into the page source to reach the browser,
   which would publish the very names it exists to hide. The
   allowlist fails closed, so an unknown vendor gets no brand label.
   ========================================================================== */

const SD_STAGE_ORDER = ['complete', 'motion', 'foundation', 'controls', 'immersion', 'accessories'];

const SD_STAGE_FROM_TAG = [
  ['complete', ['cat-bundle']],
  ['motion', ['cat-motion-simulator']],
  ['foundation', ['cat-cockpit', 'cat-seat']],
  ['controls', [
    'cat-wheelbase', 'cat-wheel', 'cat-pedals',
    'cat-shifter-handbrake', 'cat-flight-controls', 'cat-button-box'
  ]],
  ['immersion', ['cat-monitor-mount', 'cat-haptic']],
  ['accessories', ['cat-mount', 'cat-electronics', 'cat-sim-accessory']]
];

class SdPredictiveSearch extends HTMLElement {
  #controller = null;
  #abort = null;
  #timer = null;
  #active = -1;
  #items = [];

  connectedCallback() {
    this.#controller = new AbortController();
    const { signal } = this.#controller;

    this.input = this.querySelector('input[name="q"]');
    this.panel = this.querySelector('[data-sd-search-panel]');
    this.list = this.querySelector('[data-sd-search-list]');
    this.status = this.querySelector('[data-sd-search-status]');
    if (!this.input || !this.panel || !this.list) return;

    this.endpoint = this.dataset.endpoint || '/search/suggest';
    // ALLOWLIST, not a blocklist. A blocklist would have to be printed into the
    // page source to reach the browser, which publishes the names it
    // exists to hide. An allowlist of public brand names leaks nothing, and it
    // fails CLOSED: an empty or missing list shows no brand label at all.
    this.brands = (this.dataset.brands || '')
      .split(',')
      .map((s) => s.trim().toLowerCase())
      .filter(Boolean);
    this.stageLabels = this.#readLabels();

    this.input.setAttribute('role', 'combobox');
    this.input.setAttribute('aria-expanded', 'false');
    this.input.setAttribute('aria-autocomplete', 'list');
    this.input.setAttribute('autocomplete', 'off');
    if (this.list.id) this.input.setAttribute('aria-controls', this.list.id);

    this.input.addEventListener('input', this.#onInput.bind(this), { signal });
    this.input.addEventListener('keydown', this.#onKey.bind(this), { signal });
    document.addEventListener('click', this.#onDocClick.bind(this), { signal });
  }

  disconnectedCallback() {
    this.#controller?.abort();
    this.#abort?.abort();
    window.clearTimeout(this.#timer);
  }

  #readLabels() {
    try {
      return JSON.parse(this.dataset.stageLabels || '{}');
    } catch {
      return {};
    }
  }

  /* ------------------------------------------------------------- input */

  #onInput() {
    window.clearTimeout(this.#timer);
    const q = this.input.value.trim();
    if (q.length < 2) {
      this.#close();
      return;
    }
    this.#timer = window.setTimeout(() => this.#fetch(q), 200);
  }

  async #fetch(q) {
    // Abort the in-flight request first. Without this a slow response for
    // "wheel" can land after a fast one for "wheelbase" and overwrite it.
    this.#abort?.abort();
    this.#abort = new AbortController();

    const url = new URL(`${this.endpoint}.json`, window.location.origin);
    url.searchParams.set('q', q);
    url.searchParams.set('resources[type]', 'product');
    url.searchParams.set('resources[limit]', '10');
    url.searchParams.set(
      'resources[options][fields]',
      'title,product_type,variants.title,variants.sku,vendor,tag'
    );

    try {
      const response = await fetch(url, { signal: this.#abort.signal });
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      const data = await response.json();
      this.#render(data?.resources?.results?.products || [], q);
    } catch (error) {
      if (error.name === 'AbortError') return;
      // A failed lookup must leave the plain form completely usable. Close the
      // panel, say nothing alarming, and let Enter run a normal search.
      this.#close();
    }
  }

  /* ------------------------------------------------------------ render */

  /* Mirrors snippets/sd-build-stage.liquid. The endpoint cannot return a
     metafield, so source 1 is unavailable here, but source 2 (a stage-* tag)
     and source 3 (the cat-* taxonomy) both are, and they are checked in the
     same order. Without the stage-* branch, search would group a product into a
     different stage than the page it links to. */
  #stageOf(product) {
    const tags = (product.tags || []).map((t) => String(t).toLowerCase());

    for (const tag of tags) {
      if (tag.startsWith('stage-')) {
        const key = tag.slice(6);
        if (SD_STAGE_ORDER.includes(key)) return key;
      }
    }

    for (const [stage, keys] of SD_STAGE_FROM_TAG) {
      if (keys.some((k) => tags.includes(k))) return stage;
    }
    return '';
  }

  #brandOf(product) {
    const vendor = (product.vendor || '').trim();
    if (!vendor) return '';
    // Fails closed: not on the allowlist means no label.
    return this.brands.includes(vendor.toLowerCase()) ? vendor : '';
  }

  #render(products, q) {
    this.list.textContent = '';
    this.#items = [];
    this.#active = -1;

    if (!products.length) {
      this.#renderEmpty(q);
      return;
    }

    const buckets = new Map();
    for (const product of products) {
      const stage = this.#stageOf(product);
      if (!buckets.has(stage)) buckets.set(stage, []);
      buckets.get(stage).push(product);
    }

    // Ordered by the build sequence, with unclassified products last so they
    // never lead the results.
    const order = [...SD_STAGE_ORDER.filter((s) => buckets.has(s))];
    if (buckets.has('')) order.push('');

    for (const stage of order) {
      const group = document.createElement('li');
      group.className = 'sd-search__group';
      group.setAttribute('role', 'presentation');

      const label = this.stageLabels[stage];
      if (label) {
        const heading = document.createElement('p');
        heading.className = 'sd-search__group-label sd-label';
        heading.textContent = label;
        group.appendChild(heading);
      }

      const inner = document.createElement('ul');
      inner.setAttribute('role', 'presentation');
      inner.className = 'sd-search__group-list';

      for (const product of buckets.get(stage)) {
        inner.appendChild(this.#option(product));
      }

      group.appendChild(inner);
      this.list.appendChild(group);
    }

    this.#announce(products.length);
    this.#open();
  }

  #option(product) {
    const li = document.createElement('li');
    li.className = 'sd-search__opt';
    li.id = `sd-search-opt-${this.#items.length}`;   // aria-activedescendant needs a real id
    li.setAttribute('role', 'option');
    li.setAttribute('aria-selected', 'false');
    li.tabIndex = -1;

    const a = document.createElement('a');
    a.className = 'sd-search__link';
    a.href = product.url || '#';

    const brand = this.#brandOf(product);
    if (brand) {
      const b = document.createElement('span');
      b.className = 'sd-search__brand sd-label';
      b.textContent = brand;          // textContent, never innerHTML
      a.appendChild(b);
    }

    const t = document.createElement('span');
    t.className = 'sd-search__title';
    t.textContent = product.title || '';
    a.appendChild(t);

    if (product.price) {
      const p = document.createElement('span');
      p.className = 'sd-search__price sd-data';
      p.textContent = product.price;
      a.appendChild(p);
    }

    li.appendChild(a);
    this.#items.push(li);
    return li;
  }

  #renderEmpty(q) {
    const li = document.createElement('li');
    li.className = 'sd-search__empty';
    li.setAttribute('role', 'presentation');
    li.textContent = (this.dataset.emptyText || 'Nothing matched __TERMS__.')
      .replace('__TERMS__', q);
    this.list.appendChild(li);
    this.#announce(0);
    this.#open();
  }

  #announce(n) {
    if (!this.status) return;
    const template = this.dataset.countText || '__COUNT__ results';
    this.status.textContent = '';
    window.requestAnimationFrame(() => {
      this.status.textContent = template.replace('__COUNT__', String(n));
    });
  }

  /* -------------------------------------------------------- open/close */

  #open() {
    this.panel.hidden = false;
    this.input.setAttribute('aria-expanded', 'true');
  }

  #close() {
    this.panel.hidden = true;
    this.input.setAttribute('aria-expanded', 'false');
    this.#active = -1;
  }

  #onDocClick(event) {
    if (!this.contains(event.target)) this.#close();
  }

  #onKey(event) {
    if (event.key === 'Escape') {
      this.#close();
      this.input.focus();
      return;
    }
    if (!this.#items.length || this.panel.hidden) return;

    if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
      event.preventDefault();
      const step = event.key === 'ArrowDown' ? 1 : -1;
      this.#active = (this.#active + step + this.#items.length) % this.#items.length;
      this.#syncActive();
      return;
    }

    if (event.key === 'Enter' && this.#active > -1) {
      const link = this.#items[this.#active].querySelector('a');
      if (link) {
        event.preventDefault();
        window.location.assign(link.href);
      }
    }
  }

  #syncActive() {
    for (let i = 0; i < this.#items.length; i++) {
      const on = i === this.#active;
      this.#items[i].setAttribute('aria-selected', String(on));
      this.#items[i].classList.toggle('sd-search__opt--on', on);
      if (on) {
        this.#items[i].scrollIntoView({ block: 'nearest' });
        if (this.#items[i].id) this.input.setAttribute('aria-activedescendant', this.#items[i].id);
      }
    }
  }
}

if (!customElements.get('sd-predictive-search')) {
  customElements.define('sd-predictive-search', SdPredictiveSearch);
}
